import { ApolloClient, ApolloProvider } from '@apollo/client';
import { assertUnreachable, call } from '@everlutionsk/helpers';
import { UiProvider } from '@everlutionsk/ui';
import { Route, Router, Routes } from '@everlutionsk/ui-router';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import sk_dayjs from 'dayjs/locale/sk';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountMenu } from './components/accountMenu';
import { DarkModeLogo, DashboardIcon, LightModeLogo } from './components/coreComponents/icons';
import ScrollToTop from './components/coreComponents/scrollToTop';
import { GlobalStyle } from './components/globalStyle';
import { DefaultLayout } from './components/layoutDefault';
import { PageNotFound } from './components/macroLayouts/pageNotFound';
import { environment } from './environment';
import { Identity, IdentityProvider, useIdentity } from './identityContext';
import { ModeContextState, useModeContext } from './modeContext';
import { ErrorPageProvider } from './navigation/errorPageProvider';
import { NavigationItem } from './navigation/navigationBar';
import { NavSectionContext } from './navigation/navSectionContext';
import { TopBar } from './navigation/topBar';
import { AccountDetail } from './pages/accountDetail';
import { AdminSecurityPassword } from './pages/adminSecurityPassword';
import { AdminUserDetail } from './pages/adminUserDetail';
import { Administrators } from './pages/administrators';
import { ConfirmAdminCredentialsPage } from './pages/confirmAdminCredentialsPage';
import { ConfirmDeletionPage } from './pages/confirmDeletionPage';
import { Dashboard } from './pages/dashboard';
import { JobDetail } from './pages/jobDetail';
import { Jobs } from './pages/jobs';
import { Logs } from './pages/logs';
import { Notifications } from './pages/notifications';
import { PendingActionsPage } from './pages/pendingActionsPage';
import { Recovery } from './pages/recovery';
import { RecoveryDetail } from './pages/recoveryDetail';
import { useTheme } from './theme';

interface AppProps {
  readonly apollo: ApolloClient<any>;
}

export function App(props: AppProps) {
  const [{ mode }] = useModeContext();
  const theme = useTheme({ isProd: environment.isProd, mode });

  return (
    <UiProvider theme={theme} flashMessages historyState>
      <ApolloProvider client={props.apollo}>
        <Router>
          <ErrorPageProvider>
            <ScrollToTop />
            <GlobalStyle />
            <Routes>
              <Route
                path="/*"
                element={
                  <IdentityProvider>
                    <ProtectedRoutes mode={mode} />
                  </IdentityProvider>
                }
              />
            </Routes>
          </ErrorPageProvider>
        </Router>
      </ApolloProvider>
    </UiProvider>
  );
}

function ProtectedRoutes(props: { mode: ModeContextState }) {
  const identity = useIdentity();
  const { t: translation } = useTranslation();

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isNavBarExpanded, setIsNavBarExpanded] = useState<boolean>(false);

  const navigation = toNavigation({ identity, translation });

  return (
    <NavSectionContext.Provider value={{ setActiveSection }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={sk_dayjs.name}>
        <DefaultLayout
          navigation={navigation}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          isNavBarExpanded={isNavBarExpanded}
          setIsNavBarExpanded={setIsNavBarExpanded}
          topBar={
            <TopBar
              identity={identity}
              isNavBarExpanded={isNavBarExpanded}
              setIsNavBarExpanded={setIsNavBarExpanded}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              variant="default"
              desktopLogo={call((): ReactNode => {
                if (props.mode === 'light') return <LightModeLogo sx={{ width: '70px' }} />;
                if (props.mode === 'dark') return <DarkModeLogo sx={{ width: '70px' }} />;
                assertUnreachable(props.mode);
              })}
              mobileLogo={call((): ReactNode => {
                if (props.mode === 'light') return <LightModeLogo sx={{ width: '40px' }} />;
                if (props.mode === 'dark') return <DarkModeLogo sx={{ width: '40px' }} />;
                assertUnreachable(props.mode);
              })}
              actions={<AccountMenu />}
            />
          }
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/accounts/:id/:tab" element={<AccountDetail />} />
            <Route path="/recovery" element={<Recovery />} />
            <Route path="/recovery/:accountId" element={<RecoveryDetail />} />
            <Route path="/pending-actions" element={<PendingActionsPage />} />
            <Route path="/administrators" element={<Administrators />} />
            <Route path="/administrators/:id/:tab" element={<AdminUserDetail />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/settings/security/password" element={<AdminSecurityPassword />} />
            <Route path="/confirm-deletion/:token" element={<ConfirmDeletionPage />} />
            <Route
              path="/confirm-admin-credentials/:token"
              element={<ConfirmAdminCredentialsPage />}
            />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </DefaultLayout>
      </LocalizationProvider>
    </NavSectionContext.Provider>
  );
}

function toNavigation({
  identity
}: {
  identity: Pick<Identity, 'organisationRoleActive' | 'role'>;
  translation: (key: string) => string;
}): NavigationItem[] {
  return [
    {
      icon: <DashboardIcon />,
      to: '/',
      label: 'Accounts',
      slot: 'main'
    },
    {
      icon: <DashboardIcon />,
      to: '/jobs',
      label: 'Jobs',
      slot: 'main'
    },
    {
      icon: <DashboardIcon />,
      to: '/logs',
      label: 'Logs',
      slot: 'main'
    },
    {
      icon: <DashboardIcon />,
      to: '/recovery',
      label: 'Recovery',
      slot: 'main'
    },
    {
      icon: <DashboardIcon />,
      to: '/pending-actions',
      label: 'Pending actions',
      slot: 'main'
    },
    {
      icon: <DashboardIcon />,
      to: '/administrators',
      label: 'Administrators',
      slot: 'main'
    },
    // Slack notification settings manage a bot token (a secret), so the page
    // and its nav entry are superAdmin-only — matching the server-side
    // requireAdminRole(ctx, 'superAdmin') gate on every adminSlack* resolver.
    ...call((): NavigationItem[] => {
      if (identity.role !== 'superAdmin') return [];

      return [
        {
          icon: <DashboardIcon />,
          to: '/notifications',
          label: 'Notifications',
          slot: 'main' as const
        }
      ];
    })
  ];
}
